import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import {
  Library, Search, Building2, Sparkles, Image as ImageIcon,
  Video, Loader2, Tag, ChevronDown, ChevronRight, Trash2, CheckSquare, Square, X, RefreshCw, HeartPulse,
  Clock, ArrowUpDown, ExternalLink, BarChart3,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import AdDetailModal from '@/components/AdDetailModal';
import CompetitorAdCountTracker from '@/components/CompetitorAdCountTracker';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (d) => {
  if (!d) return '—';
  try {
    const dt = typeof d === 'number' ? new Date(d * 1000) : new Date(String(d));
    return format(dt, 'MMM d, yyyy');
  } catch { return String(d).slice(0, 10); }
};

// "3m ago", "2h ago", "5d ago"
const fmtRelative = (iso) => {
  if (!iso) return null;
  try {
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return null;
    const s = Math.floor((Date.now() - t) / 1000);
    if (s < 60) return 'just now';
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    const d = Math.floor(s / 86400);
    if (d < 30) return `${d}d ago`;
    const mo = Math.floor(d / 30);
    if (mo < 12) return `${mo}mo ago`;
    return `${Math.floor(mo / 12)}y ago`;
  } catch { return null; }
};

// Returns just the number of full days an ad has been running (null if unknown).
const calcActiveDays = (started, ended) => {
  if (!started) return null;
  try {
    const start = typeof started === 'number' ? new Date(started * 1000) : new Date(String(started));
    const end = ended ? (typeof ended === 'number' ? new Date(ended * 1000) : new Date(String(ended))) : new Date();
    const days = Math.max(0, Math.floor((end - start) / (1000 * 60 * 60 * 24)));
    return days;
  } catch { return null; }
};

// Determines whether an ad is currently still live.
// We DEFAULT to LIVE unless we have explicit evidence the ad has ended,
// because Meta's stored `ended_on` often becomes stale after we scrape
// (advertisers silently extend campaigns). Users can re-scrape any ad
// to refresh its status. Only marks Ended when `is_active === false`.
const isAdLive = (ad) => {
  if (ad?.is_active === false) return false;
  return true;
};

// Extracts a clean human-readable domain from a URL (e.g. "mbl.app.link").
const getDomain = (link) => {
  if (!link) return null;
  try {
    const u = new URL(link);
    return (u.hostname || '').replace(/^www\./, '');
  } catch { return null; }
};

const MiniAdCard = ({ ad, onClick, onDelete, onRescrape, rescraping, selectionMode, selected, onToggleSelect }) => {
  const thumb = ad.image_urls?.[0];
  const hasMedia = !!(ad.image_urls?.length || ad.video_urls?.length);
  // Consider an ad "video" if any signal points to it: local URLs, remote URLs, or the is_video flag
  const isVideo = !!(ad.video_urls?.length || ad.video_urls_remote?.length || ad.is_video);
  // Durable video thumbnail — captured server-side at scrape time (ffmpeg), stored in GridFS.
  // Preferred over the live video URL because it never expires.
  const posterThumb = ad.poster_url || null;
  const activeDays = calcActiveDays(ad.started_on, ad.ended_on);
  const live = isAdLive(ad);
  const ctaDomain = getDomain(ad.cta_link);
  const refreshedAgo = fmtRelative(ad.status_refreshed_at || ad.pulled_at || ad.rescraped_at || ad.created_at);
  const handleCardClick = () => {
    if (selectionMode) onToggleSelect(ad.id);
    else onClick(ad);
  };
  return (
    <div
      className={`group relative rounded-xl overflow-hidden border transition-all ${
        selected
          ? 'border-[#2EB4AC] ring-2 ring-[#2EB4AC]/30 shadow-md'
          : 'border-slate-200 bg-white hover:border-[#2EB4AC]/40 hover:shadow-md'
      }`}
      data-testid={`repo-ad-${ad.id}`}
    >
      {/* Selection checkbox — visible on hover OR when selection mode is active */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onToggleSelect(ad.id); }}
        className={`absolute top-1.5 left-1.5 z-10 w-7 h-7 rounded-md flex items-center justify-center transition-all shadow-sm ${
          selected
            ? 'bg-[#2EB4AC] text-white opacity-100'
            : `bg-white/95 border border-slate-200 text-slate-400 hover:text-[#2EB4AC] hover:border-[#2EB4AC] ${selectionMode ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`
        }`}
        title={selected ? 'Deselect' : 'Select'}
        data-testid={`repo-ad-select-${ad.id}`}
      >
        {selected ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
      </button>

      {/* Top-right action stack (hidden in selection mode) */}
      {!selectionMode && (
        <div className="absolute top-1.5 right-1.5 z-10 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onRescrape(ad); }}
            disabled={rescraping}
            className="w-7 h-7 rounded-full bg-white/95 hover:bg-[#2EB4AC]/10 border border-slate-200 hover:border-[#2EB4AC] flex items-center justify-center shadow-sm disabled:opacity-50"
            title={hasMedia ? 'Re-scrape this ad (refresh media)' : 'Re-scrape this ad (recover broken media)'}
            data-testid={`repo-ad-rescrape-${ad.id}`}
          >
            {rescraping ? <Loader2 className="h-3.5 w-3.5 animate-spin text-[#2EB4AC]" /> : <RefreshCw className="h-3.5 w-3.5 text-[#2EB4AC]" />}
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDelete(ad); }}
            className="w-7 h-7 rounded-full bg-white/95 hover:bg-red-50 border border-slate-200 hover:border-red-300 flex items-center justify-center shadow-sm"
            title="Remove from repository"
            data-testid={`repo-ad-delete-${ad.id}`}
          >
            <Trash2 className="h-3.5 w-3.5 text-red-500" />
          </button>
        </div>
      )}

      <button
        type="button"
        onClick={handleCardClick}
        className="block w-full text-left"
      >
        <div className={`relative aspect-[4/5] overflow-hidden ${ad.video_urls?.length && !thumb && !posterThumb ? 'bg-gradient-to-br from-slate-800 to-slate-900' : 'bg-slate-100'}`}>
          {thumb ? (
            <img alt="" src={thumb} referrerPolicy="no-referrer" className="w-full h-full object-cover" />
          ) : posterThumb ? (
            // Durable server-generated poster — never expires
            <img alt="" src={posterThumb} referrerPolicy="no-referrer" className="w-full h-full object-cover" />
          ) : ad.video_urls?.length ? (
            // Fall back to live-video first-frame render for legacy ads without a poster
            <video
              src={`${ad.video_urls[0]}#t=0.5`}
              preload="metadata"
              muted
              playsInline
              className="w-full h-full object-cover pointer-events-none"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center gap-2 px-2 text-center">
              <ImageIcon className="h-7 w-7 text-slate-300" />
              <span className="text-[10px] text-slate-400 leading-tight">Media missing<br /><span className="text-[#2EB4AC]">tap re-scrape ↗</span></span>
            </div>
          )}
          {/* Center play chip on video cards for scanability, even when the frame fails to load */}
          {isVideo && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-9 h-9 rounded-full border-2 border-white/80 bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-lg">
                <Video className="h-4 w-4 text-white" />
              </div>
            </div>
          )}

          {/* Bottom scrim for badge legibility */}
          {(hasMedia || activeDays != null) && (
            <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-black/55 via-black/10 to-transparent pointer-events-none" />
          )}

          {/* Bottom-left: 🔥 days badge */}
          {activeDays != null && (
            <div
              className="absolute bottom-1.5 left-1.5 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-white/95 backdrop-blur-sm shadow-sm text-[10px] font-bold text-slate-800"
              title={`Active for ${activeDays} day${activeDays === 1 ? '' : 's'}`}
              data-testid={`repo-active-days-${ad.id}`}
            >
              <span className="text-[10px] leading-none">🔥</span>
              <span>{activeDays}d</span>
            </div>
          )}

          {/* Bottom-right: media type chip */}
          {hasMedia && (
            <div
              className="absolute bottom-1.5 right-1.5 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-black/60 backdrop-blur-sm text-[9px] font-bold tracking-wider text-white uppercase"
              data-testid={`repo-media-chip-${ad.id}`}
            >
              {isVideo ? <><Video className="h-2.5 w-2.5" /> Video</> : <><ImageIcon className="h-2.5 w-2.5" /> Image</>}
            </div>
          )}
        </div>
        <div className="p-2.5">
          <div className="flex items-center gap-1.5 mb-1">
            {/* Live / Ended status pill */}
            <span
              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] font-bold ${
                live ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500 border border-slate-200'
              }`}
              data-testid={`repo-live-status-${ad.id}`}
              title={live ? 'This ad appears to be still live in Meta Ad Library' : 'This ad has ended'}
            >
              {live && <span className="w-1 h-1 rounded-full bg-emerald-500" />}
              {live ? 'Live' : 'Ended'}
            </span>
            <p className="text-xs font-semibold text-slate-900 truncate flex-1">{ad.page_name || ad.source_brand}</p>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {ad.analysis && <Badge className="bg-violet-100 text-violet-700 text-[9px] h-4 py-0 px-1.5">Analyzed</Badge>}
            {ad.learning && <Badge className="bg-amber-100 text-amber-700 text-[9px] h-4 py-0 px-1.5">Learning</Badge>}
            {refreshedAgo && (
              <span
                className="text-[9px] text-slate-400 ml-auto"
                title={`Ad status last refreshed ${refreshedAgo}${ad.status_refreshed_at ? ` (${fmtDate(ad.status_refreshed_at)})` : ''}`}
                data-testid={`repo-refreshed-${ad.id}`}
              >
                Refreshed {refreshedAgo}
              </span>
            )}
          </div>
        </div>
      </button>

      {/* CTA link footer — preserved from Radar / Meta Ad Library */}
      {(ad.cta_text || ctaDomain) && (
        <div
          className="px-2.5 py-1.5 bg-slate-50/70 border-t border-slate-100 flex items-center justify-between gap-1.5"
          onClick={(e) => e.stopPropagation()}
        >
          <span className="text-[9px] text-slate-500 font-medium tracking-wider uppercase truncate flex-1" title={ad.cta_link || ''}>
            {ctaDomain || '—'}
          </span>
          {ad.cta_text && ad.cta_link && (
            <a
              href={ad.cta_link}
              target="_blank"
              rel="noreferrer"
              className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-200 hover:bg-[#2EB4AC] hover:text-white text-[10px] font-semibold text-slate-700 transition-colors"
              data-testid={`repo-cta-${ad.id}`}
              title={`Open landing page: ${ad.cta_link}`}
            >
              {ad.cta_text}
              <ExternalLink className="h-2.5 w-2.5" />
            </a>
          )}
          {ad.cta_text && !ad.cta_link && (
            <span className="shrink-0 px-2 py-0.5 rounded-md bg-slate-200 text-[10px] font-semibold text-slate-500">{ad.cta_text}</span>
          )}
        </div>
      )}
    </div>
  );
};

export default function CompetitorRepositoryPage() {
  // Two views hosted on the same route — the classic "Library" grid, and the new
  // per-brand Ad Count Tracker matrix. Kept in local state instead of routes to
  // preserve grid scroll position when the user toggles back.
  const [pageTab, setPageTab] = useState('library'); // 'library' | 'tracker'
  const [groups, setGroups] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [activeBrand, setActiveBrand] = useState(null);
  const [selectedAd, setSelectedAd] = useState(null);
  const [analyzingId, setAnalyzingId] = useState(null);
  const [rescrapingId, setRescrapingId] = useState(null);
  const [expandedBrands, setExpandedBrands] = useState({}); // {brandName: true} — default ALL collapsed
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  // Filter + sort state (applied client-side across all groups)
  const [mediaFilter, setMediaFilter] = useState('all'); // all | image | video
  const [activeTimeFilter, setActiveTimeFilter] = useState('any'); // any | lt7 | 7to30 | 30to90 | gt90
  const [sortBy, setSortBy] = useState('recent'); // recent | oldest | longest | shortest
  const selectionMode = selectedIds.length > 0;

  const fetchRepo = async () => {
    try {
      const r = await fetch(`${API_URL}/api/competitor-ads/repository${activeBrand ? `?brand=${encodeURIComponent(activeBrand)}` : ''}`, { credentials: 'include' });
      if (!r.ok) throw new Error();
      const j = await r.json();
      setGroups(j.groups || []);
      setTotal(j.total || 0);
    } catch {
      toast.error('Could not load repository');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRepo(); /* eslint-disable-next-line */ }, [activeBrand]);

  // Patch an ad across the groups list + selectedAd state (keeps modal in sync)
  const patchAd = (adId, patch) => {
    setGroups(prev => prev.map(g => ({
      ...g,
      items: g.items.map(x => x.id === adId ? { ...x, ...patch } : x),
    })));
    setSelectedAd(prev => prev && prev.id === adId ? { ...prev, ...patch } : prev);
  };

  const analyzeAd = async (ad) => {
    setAnalyzingId(ad.id);
    try {
      const r = await fetch(`${API_URL}/api/competitor-ads/${ad.id}/analyze`, { method: 'POST', credentials: 'include' });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Analysis failed');
      toast.success('Analyzed with GPT-4o');
      patchAd(ad.id, { analysis: j.analysis, analysis_id: j.analysis_id });
      // bump analyzed counter in the relevant group
      setGroups(prev => prev.map(g => ({
        ...g,
        analyzed_count: g.items.some(x => x.id === ad.id) ? (g.analyzed_count || 0) + 1 : g.analyzed_count,
      })));
    } catch (e) {
      toast.error(`Analysis failed: ${e.message}`);
    } finally {
      setAnalyzingId(null);
    }
  };

  const saveLearning = async (adId, learningText) => {
    try {
      const r = await fetch(`${API_URL}/api/competitor-ads/${adId}/learning`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ learning: learningText }),
      });
      if (!r.ok) throw new Error();
      toast.success('Learning saved');
      patchAd(adId, { learning: learningText, learning_updated_at: new Date().toISOString() });
    } catch {
      toast.error('Could not save learning');
    }
  };

  const rephraseLearning = async (adId) => {
    try {
      const r = await fetch(`${API_URL}/api/competitor-ads/${adId}/rephrase-learning`, {
        method: 'POST', credentials: 'include',
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Rephrase failed');
      toast.success('Actionable summary generated');
      patchAd(adId, { learning_rephrased: j.learning_rephrased, learning_rephrased_at: j.learning_rephrased_at });
    } catch (e) {
      toast.error(`Rephrase failed: ${e.message || ''}`);
    }
  };

  // Kicks off an async rescrape and polls until done. Handles non-JSON responses
  // (proxy timeouts return HTML), long Apify waits, and returns a normalized result.
  const runRescrapeJob = async (adId, onTick) => {
    const safeJson = async (resp) => {
      const text = await resp.text();
      try { return JSON.parse(text); } catch { return { detail: `Server returned non-JSON (${resp.status})` }; }
    };
    const startRes = await fetch(`${API_URL}/api/competitor-ads/${adId}/rescrape/start`, {
      method: 'POST',
      credentials: 'include',
    });
    const startJson = await safeJson(startRes);
    if (!startRes.ok) throw new Error(startJson.detail || `Start failed (HTTP ${startRes.status})`);
    const jobId = startJson.job_id;
    // Poll every 2.5s for up to 5 minutes.
    const MAX_ATTEMPTS = 120;
    for (let a = 0; a < MAX_ATTEMPTS; a++) {
      await new Promise(res => setTimeout(res, 2500));
      const sr = await fetch(`${API_URL}/api/competitor-ads/rescrape/status/${jobId}`, { credentials: 'include' });
      const sj = await safeJson(sr);
      if (!sr.ok) throw new Error(sj.detail || `Status poll failed (HTTP ${sr.status})`);
      if (sj.status === 'success' && sj.result) return sj.result;
      if (sj.status === 'error') throw new Error(sj.error || 'Rescrape failed');
      if (onTick) onTick(a, MAX_ATTEMPTS);
    }
    throw new Error('Rescrape timed out after 5 minutes');
  };

  const rescrapeAd = async (ad) => {
    if (rescrapingId) return;
    setRescrapingId(ad.id);
    const t = toast.loading('Re-fetching from Meta Ad Library…');
    try {
      const j = await runRescrapeJob(ad.id);
      await fetchRepo();
      if ((j.image_count || 0) + (j.video_count || 0) > 0) {
        toast.success(`Re-scraped: ${j.image_count} image${j.image_count === 1 ? '' : 's'}${j.video_count ? `, ${j.video_count} video${j.video_count === 1 ? '' : 's'}` : ''}`, { id: t });
      } else {
        toast.warning(j.message || 'Ad found but Meta returned no media — it may be inactive', { id: t });
      }
    } catch (e) {
      toast.error(`Re-scrape failed: ${e.message || ''}`, { id: t });
    } finally {
      setRescrapingId(null);
    }
  };

  const deleteAd = async (ad) => {    if (!window.confirm(`Remove this ad from "${ad.brand_assigned}" repository? It will be permanently deleted.`)) return;
    try {
      const r = await fetch(`${API_URL}/api/competitor-ads/${ad.id}`, { method: 'DELETE', credentials: 'include' });
      if (!r.ok) throw new Error();
      // Remove locally + decrement brand count
      setGroups(prev => prev
        .map(g => ({
          ...g,
          items: g.items.filter(x => x.id !== ad.id),
          count: g.items.some(x => x.id === ad.id) ? Math.max(0, (g.count || 0) - 1) : g.count,
          analyzed_count: ad.analysis && g.items.some(x => x.id === ad.id) ? Math.max(0, (g.analyzed_count || 0) - 1) : g.analyzed_count,
        }))
        .filter(g => g.count > 0)
      );
      setTotal(prev => Math.max(0, prev - 1));
      toast.success('Removed from repository');
    } catch {
      toast.error('Could not delete ad');
    }
  };

  const toggleBrand = (brand) => setExpandedBrands(prev => ({ ...prev, [brand]: !prev[brand] }));

  const toggleSelect = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const toggleSelectGroup = (group) => {
    const groupIds = group.items.map(a => a.id);
    const allSelected = groupIds.every(id => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds(prev => prev.filter(id => !groupIds.includes(id)));
    } else {
      setSelectedIds(prev => Array.from(new Set([...prev, ...groupIds])));
    }
  };

  const clearSelection = () => setSelectedIds([]);

  const bulkDelete = async () => {
    if (selectedIds.length === 0 || bulkDeleting) return;
    if (!window.confirm(`Permanently delete ${selectedIds.length} ${selectedIds.length === 1 ? 'ad' : 'ads'} from the repository? This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      const r = await fetch(`${API_URL}/api/competitor-ads/bulk-delete`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: selectedIds }),
      });
      if (!r.ok) throw new Error();
      const j = await r.json();
      const idsSet = new Set(selectedIds);
      setGroups(prev => prev
        .map(g => {
          const remaining = g.items.filter(x => !idsSet.has(x.id));
          const removedCount = g.items.length - remaining.length;
          const removedAnalyzed = g.items.filter(x => idsSet.has(x.id) && x.analysis).length;
          return {
            ...g,
            items: remaining,
            count: Math.max(0, (g.count || 0) - removedCount),
            analyzed_count: Math.max(0, (g.analyzed_count || 0) - removedAnalyzed),
          };
        })
        .filter(g => g.count > 0)
      );
      setTotal(prev => Math.max(0, prev - (j.deleted || selectedIds.length)));
      setSelectedIds([]);
      toast.success(`${j.deleted || selectedIds.length} ${(j.deleted || selectedIds.length) === 1 ? 'ad' : 'ads'} removed`);
    } catch {
      toast.error('Bulk delete failed');
    } finally {
      setBulkDeleting(false);
    }
  };

  const filteredGroups = useMemo(() => {
    const q = filter.trim().toLowerCase();
    // Sort helpers
    const pickTs = (a) => {
      const raw = a.started_on || a.start_date_iso || a.first_seen_at || a.created_at || 0;
      const t = raw ? new Date(typeof raw === 'number' ? raw * 1000 : raw).getTime() : 0;
      return Number.isNaN(t) ? 0 : t;
    };
    const pickDays = (a) => calcActiveDays(a.started_on, a.ended_on) ?? -1;

    const matchesActiveTime = (a) => {
      if (activeTimeFilter === 'any') return true;
      const d = calcActiveDays(a.started_on, a.ended_on);
      if (d == null) return false;
      if (activeTimeFilter === 'lt7') return d < 7;
      if (activeTimeFilter === '7to30') return d >= 7 && d < 30;
      if (activeTimeFilter === '30to90') return d >= 30 && d < 90;
      if (activeTimeFilter === 'gt90') return d >= 90;
      return true;
    };
    const isVid = (a) => !!(a.video_urls?.length || a.video_urls_remote?.length || a.is_video);
    const matchesMedia = (a) => {
      if (mediaFilter === 'all') return true;
      if (mediaFilter === 'video') return isVid(a);
      // image = not a video
      return !isVid(a);
    };
    const matchesSearch = (a) => {
      if (!q) return true;
      return (a.page_name || '').toLowerCase().includes(q) || (a.body_text || '').toLowerCase().includes(q);
    };

    return groups
      .map(g => {
        let items = g.items.filter(a => matchesSearch(a) && matchesMedia(a) && matchesActiveTime(a));
        if (sortBy === 'oldest') items = [...items].sort((a, b) => pickTs(a) - pickTs(b));
        else if (sortBy === 'longest') items = [...items].sort((a, b) => pickDays(b) - pickDays(a));
        else if (sortBy === 'shortest') items = [...items].sort((a, b) => (pickDays(a) < 0 ? Infinity : pickDays(a)) - (pickDays(b) < 0 ? Infinity : pickDays(b)));
        else items = [...items].sort((a, b) => pickTs(b) - pickTs(a)); // recent
        return { ...g, items, count: items.length };
      })
      .filter(g => (q ? g.brand.toLowerCase().includes(q) || g.items.length > 0 : g.items.length > 0));
  }, [groups, filter, mediaFilter, activeTimeFilter, sortBy]);

  // Live counts across all groups for filter labels
  const allAds = useMemo(() => groups.flatMap(g => g.items), [groups]);
  const filterCounts = useMemo(() => {
    const c = { total: allAds.length, image: 0, video: 0, lt7: 0, '7to30': 0, '30to90': 0, gt90: 0 };
    for (const a of allAds) {
      const isVid = !!(a.video_urls?.length || a.video_urls_remote?.length || a.is_video);
      if (isVid) c.video++;
      else c.image++;
      const d = calcActiveDays(a.started_on, a.ended_on);
      if (d != null) {
        if (d < 7) c.lt7++;
        else if (d < 30) c['7to30']++;
        else if (d < 90) c['30to90']++;
        else c.gt90++;
      }
    }
    return c;
  }, [allAds]);
  const activeTimeLabel = {
    any: 'Any duration',
    lt7: '< 7 days',
    '7to30': '7 – 30 days',
    '30to90': '30 – 90 days',
    gt90: '90+ days',
  };
  const sortLabel = {
    recent: 'Most recent',
    oldest: 'Oldest first',
    longest: 'Longest running',
    shortest: 'Shortest running',
  };

  const totalAnalyzed = groups.reduce((s, g) => s + (g.analyzed_count || 0), 0);
  const brokenAds = useMemo(
    () => groups.flatMap(g => g.items.filter(a => !(a.image_urls?.length) && !(a.video_urls?.length))),
    [groups]
  );

  const rescrapeAllBroken = async () => {
    if (rescrapingId || brokenAds.length === 0) return;
    if (!window.confirm(`Re-scrape ${brokenAds.length} broken ad${brokenAds.length === 1 ? '' : 's'} from Meta Ad Library? This may take a minute or two.`)) return;
    let recovered = 0;
    let failed = 0;
    const t = toast.loading(`Re-scraping 0 / ${brokenAds.length}…`);
    for (let i = 0; i < brokenAds.length; i++) {
      const ad = brokenAds[i];
      setRescrapingId(ad.id);
      try {
        const j = await runRescrapeJob(ad.id);
        if ((j.image_count || 0) + (j.video_count || 0) > 0) recovered += 1;
        else failed += 1;
      } catch {
        failed += 1;
      }
      toast.loading(`Re-scraping ${i + 1} / ${brokenAds.length}…`, { id: t });
    }
    setRescrapingId(null);
    await fetchRepo();
    if (recovered > 0) {
      toast.success(`Recovered ${recovered} ad${recovered === 1 ? '' : 's'}${failed ? ` · ${failed} unrecoverable` : ''}`, { id: t });
    } else {
      toast.error(`No ads recovered — Meta may have removed them`, { id: t });
    }
  };

  // Ads whose Live/Ended status hasn't been refreshed in the last 24h.
  // Auto-picked as candidates for a bulk status refresh.
  const staleAds = useMemo(() => {
    const cutoff = Date.now() - 24 * 60 * 60 * 1000;
    return groups.flatMap(g => g.items.filter(a => {
      const ts = a.status_refreshed_at || a.rescraped_at || a.pulled_at || a.created_at;
      if (!ts) return true;
      try { return new Date(ts).getTime() < cutoff; } catch { return true; }
    }));
  }, [groups]);

  // Per-brand refresh-freshness stats powering the top ticker bar.
  const tickerItems = useMemo(() => {
    return groups.map(g => {
      let oldest = Infinity;
      let newest = 0;
      for (const a of g.items) {
        const ts = a.status_refreshed_at || a.rescraped_at || a.pulled_at || a.created_at;
        if (!ts) continue;
        try {
          const t = new Date(ts).getTime();
          if (Number.isFinite(t)) {
            if (t < oldest) oldest = t;
            if (t > newest) newest = t;
          }
        } catch { /* ignore malformed dates */ }
      }
      return {
        brand: g.brand,
        count: g.items.length,
        oldestAgo: Number.isFinite(oldest) ? fmtRelative(new Date(oldest).toISOString()) : null,
        newestAgo: newest ? fmtRelative(new Date(newest).toISOString()) : null,
      };
    }).sort((a, b) => b.count - a.count);
  }, [groups]);

  const [refreshingStatuses, setRefreshingStatuses] = useState(false);

  // ==== Duration Summary card state (collapsible) ====
  const [summaryOpen, setSummaryOpen] = useState(true);
  const [summaryLiveOnly, setSummaryLiveOnly] = useState(false);
  const [summarySortBy, setSummarySortBy] = useState('total'); // 'brand' | 'lt7' | '7to30' | '30to90' | 'gt90' | 'total'
  const [summarySortDir, setSummarySortDir] = useState('desc'); // 'asc' | 'desc'

  // Build the summary rows: one per brand_assigned, with counts per bucket.
  const summaryRows = useMemo(() => {
    const byBrand = new Map();
    for (const g of groups) {
      for (const ad of g.items) {
        if (summaryLiveOnly && !isAdLive(ad)) continue;
        const brand = ad.brand_assigned || g.brand || 'Unassigned';
        if (!byBrand.has(brand)) byBrand.set(brand, { brand, lt7: 0, '7to30': 0, '30to90': 0, gt90: 0, total: 0 });
        const row = byBrand.get(brand);
        row.total += 1;
        const d = calcActiveDays(ad.started_on, ad.ended_on);
        if (d == null) continue; // Uncategorised — still counted in total
        if (d < 7) row.lt7 += 1;
        else if (d < 30) row['7to30'] += 1;
        else if (d < 90) row['30to90'] += 1;
        else row.gt90 += 1;
      }
    }
    let rows = Array.from(byBrand.values());
    const key = summarySortBy;
    const dir = summarySortDir === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      if (key === 'brand') {
        return a.brand.localeCompare(b.brand) * dir;
      }
      return ((a[key] ?? 0) - (b[key] ?? 0)) * dir;
    });
    return rows;
  }, [groups, summaryLiveOnly, summarySortBy, summarySortDir]);

  // Aggregate footer totals row
  const summaryFooter = useMemo(() => {
    return summaryRows.reduce((acc, r) => {
      acc.lt7 += r.lt7; acc['7to30'] += r['7to30']; acc['30to90'] += r['30to90']; acc.gt90 += r.gt90; acc.total += r.total;
      return acc;
    }, { lt7: 0, '7to30': 0, '30to90': 0, gt90: 0, total: 0 });
  }, [summaryRows]);

  const toggleSummarySort = (key) => {
    if (summarySortBy === key) {
      setSummarySortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSummarySortBy(key);
      setSummarySortDir(key === 'brand' ? 'asc' : 'desc');
    }
  };

  // Click a bucket cell → jump to the live grid filtered by brand + duration bucket.
  const jumpToBucket = (brand, bucket) => {
    setActiveBrand(brand);
    setActiveTimeFilter(bucket);
    setExpandedBrands(prev => ({ ...prev, [brand]: true }));
    // Scroll to the brand group after a tick so the render has caught up.
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="repo-group-${brand}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 60);
  };

  const refreshAllStatuses = async () => {
    if (refreshingStatuses || staleAds.length === 0) return;
    if (!window.confirm(`Refresh Live/Ended status for ${staleAds.length} ad${staleAds.length === 1 ? '' : 's'}? Each ad is re-verified against Meta Ad Library — this may take a minute or two.`)) return;
    setRefreshingStatuses(true);
    let updated = 0;
    let failed = 0;
    const t = toast.loading(`Refreshing status 0 / ${staleAds.length}…`);
    for (let i = 0; i < staleAds.length; i++) {
      const ad = staleAds[i];
      try {
        await runRescrapeJob(ad.id);
        updated += 1;
      } catch { failed += 1; }
      toast.loading(`Refreshing status ${i + 1} / ${staleAds.length}…`, { id: t });
    }
    await fetchRepo();
    setRefreshingStatuses(false);
    if (updated > 0) toast.success(`${updated} statuses refreshed${failed ? ` · ${failed} skipped` : ''}`, { id: t });
    else toast.error('Could not refresh any statuses', { id: t });
  };

  const [healingLibrary, setHealingLibrary] = useState(false);
  const [healProgress, setHealProgress] = useState(null); // { processed, total, rehosted_images, rehosted_videos, current_ad }

  const pollBackfillJob = async (jobId, toastId) => {
    // Poll every 2s until the job finishes. Cloudflare has no timeout on responses
    // that came back quickly; only the polling requests stay open.
    let stalled = 0;
    while (true) {
      await new Promise(r => setTimeout(r, 2000));
      try {
        const r = await fetch(`${API_URL}/api/competitor-ads/backfill-media/status/${jobId}`, { credentials: 'include' });
        if (!r.ok) throw new Error('lost job status');
        const j = await r.json();
        setHealProgress(j);
        const pct = j.total > 0 ? Math.round((j.processed / j.total) * 100) : 0;
        const label = j.current_ad ? ` · ${j.current_ad}` : '';
        toast.loading(`Healing ${pct}% (${j.processed}/${j.total})${label}`, { id: toastId });
        if (j.status === 'completed') {
          const bits = [];
          if (j.rehosted_images) bits.push(`${j.rehosted_images} image${j.rehosted_images === 1 ? '' : 's'}`);
          if (j.rehosted_videos) bits.push(`${j.rehosted_videos} video${j.rehosted_videos === 1 ? '' : 's'}`);
          const summary = bits.length
            ? `Re-hosted ${bits.join(' + ')} across ${j.processed} ad${j.processed === 1 ? '' : 's'}`
            : `Checked ${j.processed} ad${j.processed === 1 ? '' : 's'} — nothing needed healing`;
          toast.success(summary, { id: toastId });
          return;
        }
        if (j.status === 'failed') {
          throw new Error(j.error || 'Heal job failed');
        }
        stalled = 0;
      } catch (e) {
        stalled += 1;
        if (stalled >= 5) {
          throw e;
        }
      }
    }
  };

  const healLibrary = async () => {
    if (healingLibrary) return;
    if (!window.confirm('Heal the library? This re-hosts every ad\u2019s images and videos in durable storage so they never break across deploys.\n\nRuns in the background — you can keep working.')) return;
    setHealingLibrary(true);
    setHealProgress(null);
    const t = toast.loading('Starting heal job…');
    try {
      const r = await fetch(`${API_URL}/api/competitor-ads/backfill-media/start`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!r.ok) {
        const err = await r.text();
        throw new Error(err || 'Could not start heal job');
      }
      const job = await r.json();
      await pollBackfillJob(job.id, t);
      await fetchRepo();
    } catch (e) {
      toast.error(e.message || 'Could not heal library', { id: t });
    } finally {
      setHealingLibrary(false);
      setHealProgress(null);
    }
  };

  return (
    <div className="space-y-8 p-4 md:p-6 max-w-[1400px] mx-auto" data-testid="competitor-repository-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/20">
            <Library className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Competitor Repository</h1>
            <p className="text-sm text-slate-500">Ads filed by brand from Competitor Radar · your long-term reference library</p>
          </div>
        </div>
        <div className="flex items-center gap-3 text-xs text-slate-500 flex-wrap">
          <span className="inline-flex items-center gap-1"><Building2 className="h-3.5 w-3.5" />{groups.length} brand{groups.length === 1 ? '' : 's'}</span>
          <span className="inline-flex items-center gap-1"><ImageIcon className="h-3.5 w-3.5" />{total} ads</span>
          <span className="inline-flex items-center gap-1"><Sparkles className="h-3.5 w-3.5 text-violet-500" />{totalAnalyzed} analyzed</span>
          {staleAds.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 px-2.5 border-blue-300 text-blue-700 hover:bg-blue-50 hover:text-blue-800"
              onClick={refreshAllStatuses}
              disabled={refreshingStatuses}
              data-testid="repo-refresh-statuses"
              title={`Re-verify Live/Ended status against Meta Ad Library for ${staleAds.length} ad${staleAds.length === 1 ? '' : 's'} last checked > 24h ago`}
            >
              {refreshingStatuses ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1" />}
              Refresh {staleAds.length} status{staleAds.length === 1 ? '' : 'es'}
            </Button>
          )}
        </div>
      </div>

      {/* Page-level tabs: switch between the classic ads library and the
          new per-brand ad-count tracker matrix. */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit" data-testid="repo-page-tabs">
        {[
          { k: 'library', label: 'Ad Library', I: Library },
          { k: 'tracker', label: 'Ad Count Tracker', I: BarChart3 },
        ].map(t => (
          <button
            key={t.k}
            onClick={() => setPageTab(t.k)}
            data-testid={`repo-page-tab-${t.k}`}
            className={`px-4 py-1.5 rounded-md text-sm font-medium flex items-center gap-2 transition
              ${pageTab === t.k
                ? 'bg-white text-slate-900 shadow'
                : 'text-slate-600 hover:text-slate-900'}`}
          >
            <t.I className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>

      {/* Tracker view — completely separate from the library grid so it never
          contends for space or state with the ad browser. */}
      {pageTab === 'tracker' && (
        <div className="rounded-xl bg-slate-950 border border-slate-800 p-4">
          <CompetitorAdCountTracker />
        </div>
      )}

      {pageTab === 'library' && (<>

      {tickerItems.length > 0 && (
        <div className="pt-2">
          <style>{`
            @keyframes repo-ticker-scroll {
              0% { transform: translateX(0); }
              100% { transform: translateX(-50%); }
            }
            .repo-ticker-track {
              animation: repo-ticker-scroll 45s linear infinite;
            }
            .repo-ticker:hover .repo-ticker-track { animation-play-state: paused; }
          `}</style>
          <div
            className="repo-ticker relative flex items-center gap-3 rounded-xl border border-slate-200 bg-gradient-to-r from-slate-50 via-white to-slate-50 overflow-hidden shadow-sm"
            data-testid="repo-ticker"
          >
            <div className="shrink-0 flex items-center gap-2 pl-4 pr-4 py-2.5 border-r border-slate-200 bg-white z-10 relative">
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#2EB4AC] to-emerald-400 flex items-center justify-center shrink-0">
                <RefreshCw className={`h-3 w-3 text-white ${refreshingStatuses ? 'animate-spin' : ''}`} />
              </div>
              <span className="text-[11px] font-bold text-slate-700 tracking-wide uppercase">Status</span>
              {/* Fade-out gradient so ticker items don't visually collide with the label */}
              <div className="absolute right-[-16px] top-0 bottom-0 w-4 bg-gradient-to-r from-white to-transparent pointer-events-none" />
            </div>
            <div className="flex-1 min-w-0 overflow-hidden py-2.5 relative">
              <div className="repo-ticker-track inline-flex items-center gap-8 whitespace-nowrap">
                {/* Duplicated for a seamless loop */}
                {[0, 1].map(pass => (
                  <div key={pass} className="inline-flex items-center gap-8 pr-8" aria-hidden={pass === 1}>
                    {tickerItems.map(t => (
                      <span
                        key={`${pass}-${t.brand}`}
                        className="inline-flex items-center gap-2 text-[12px] text-slate-600"
                        data-testid={pass === 0 ? `repo-ticker-item-${t.brand}` : undefined}
                      >
                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                        <span className="font-semibold text-slate-800">{t.brand}</span>
                        <span className="text-slate-400">·</span>
                        <span>{t.count} ad{t.count === 1 ? '' : 's'}</span>
                        {t.oldestAgo && <><span className="text-slate-400">·</span><span>oldest check <span className="font-medium text-slate-700">{t.oldestAgo}</span></span></>}
                        {t.newestAgo && <><span className="text-slate-400">·</span><span>refreshed <span className="font-medium text-slate-700">{t.newestAgo}</span></span></>}
                      </span>
                    ))}
                  </div>
                ))}
              </div>
              {/* Right fade so items don't clip harshly into the refresh button */}
              <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-r from-transparent to-white pointer-events-none" />
            </div>
            <button
              type="button"
              onClick={refreshAllStatuses}
              disabled={refreshingStatuses || staleAds.length === 0}
              className="shrink-0 inline-flex items-center gap-1.5 h-8 px-3 mr-3 rounded-md text-[11px] font-semibold text-white bg-[#2EB4AC] hover:bg-[#26978f] disabled:opacity-50 disabled:cursor-not-allowed transition-colors z-10 relative"
              data-testid="repo-ticker-refresh"
              title={staleAds.length ? `Re-verify Live/Ended for ${staleAds.length} stale ad${staleAds.length === 1 ? '' : 's'}` : 'All statuses are fresh (< 24h)'}
            >
              {refreshingStatuses ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
              {staleAds.length > 0 ? `Refresh ${staleAds.length}` : 'All fresh'}
            </button>
          </div>
        </div>
      )}

      {/* Duration Summary — collapsible card */}
      <Card className="border-slate-200 rounded-2xl overflow-hidden" data-testid="repo-summary-card">
        <button
          type="button"
          onClick={() => setSummaryOpen(o => !o)}
          className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-slate-50 transition-colors"
          data-testid="repo-summary-toggle"
        >
          <div className="flex items-center gap-2.5 text-left">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-100 to-fuchsia-100 flex items-center justify-center shrink-0">
              <BarChart3 className="h-4 w-4 text-violet-600" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-slate-900 tracking-tight">Competitor Ad Duration Summary</h2>
              <p className="text-[11px] text-slate-500">
                {summaryRows.length} {summaryRows.length === 1 ? 'brand' : 'brands'} · {summaryFooter.total} {summaryFooter.total === 1 ? 'ad' : 'ads'} bucketed by runtime
                {summaryLiveOnly && ' · Live only'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <label
              className="inline-flex items-center gap-1.5 cursor-pointer select-none"
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="checkbox"
                checked={summaryLiveOnly}
                onChange={(e) => setSummaryLiveOnly(e.target.checked)}
                className="rounded border-slate-300 text-[#2EB4AC] focus:ring-[#2EB4AC]"
                data-testid="repo-summary-live-only"
              />
              <span className="text-[11px] font-medium text-slate-600">Live only</span>
            </label>
            {summaryOpen ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
          </div>
        </button>

        {summaryOpen && (
          <div className="border-t border-slate-100 px-4 pb-4 pt-2 overflow-x-auto">
            {summaryRows.length === 0 ? (
              <p className="text-xs text-slate-400 py-4 text-center">No ads to summarize yet.</p>
            ) : (
              <table className="w-full text-sm" data-testid="repo-summary-table">
                <thead>
                  <tr className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">
                    {[
                      { key: 'brand', label: 'Competitor Brand', align: 'left', className: 'sticky left-0 bg-white z-10' },
                      { key: 'lt7', label: '< 7 Days', align: 'right' },
                      { key: '7to30', label: '7 – 30 Days', align: 'right' },
                      { key: '30to90', label: '30 – 90 Days', align: 'right' },
                      { key: 'gt90', label: '90+ Days', align: 'right' },
                      { key: 'total', label: 'Total', align: 'right' },
                    ].map(col => (
                      <th
                        key={col.key}
                        className={`px-3 py-2.5 ${col.align === 'right' ? 'text-right' : 'text-left'} ${col.className || ''}`}
                      >
                        <button
                          type="button"
                          onClick={() => toggleSummarySort(col.key)}
                          className={`inline-flex items-center gap-1 hover:text-slate-900 transition-colors ${
                            summarySortBy === col.key ? 'text-slate-900' : ''
                          }`}
                          data-testid={`repo-summary-sort-${col.key}`}
                        >
                          {col.label}
                          <ArrowUpDown
                            className={`h-3 w-3 ${summarySortBy === col.key ? 'text-[#2EB4AC]' : 'text-slate-300'} ${
                              summarySortBy === col.key && summarySortDir === 'asc' ? 'rotate-180' : ''
                            } transition-transform`}
                          />
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {summaryRows.map(row => (
                    <tr key={row.brand} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60 transition-colors group" data-testid={`repo-summary-row-${row.brand}`}>
                      <td className="px-3 py-2.5 sticky left-0 bg-white group-hover:bg-slate-50/60 z-10 font-semibold text-slate-800">
                        <button
                          type="button"
                          onClick={() => { setActiveBrand(row.brand); setActiveTimeFilter('any'); }}
                          className="hover:text-[#2EB4AC] transition-colors text-left"
                          title={`Filter grid to ${row.brand}`}
                        >
                          {row.brand}
                        </button>
                      </td>
                      {[
                        { key: 'lt7', bucket: 'lt7' },
                        { key: '7to30', bucket: '7to30' },
                        { key: '30to90', bucket: '30to90' },
                        { key: 'gt90', bucket: 'gt90' },
                      ].map(col => (
                        <td key={col.key} className="px-3 py-2.5 text-right tabular-nums">
                          {row[col.key] > 0 ? (
                            <button
                              type="button"
                              onClick={() => jumpToBucket(row.brand, col.bucket)}
                              className="inline-flex items-center justify-center min-w-[2.25rem] h-7 px-2 rounded-md text-xs font-semibold text-slate-700 hover:bg-[#2EB4AC] hover:text-white transition-colors"
                              data-testid={`repo-summary-cell-${row.brand}-${col.key}`}
                              title={`View ${row[col.key]} ${row.brand} ad${row[col.key] === 1 ? '' : 's'} in this bucket`}
                            >
                              {row[col.key]}
                            </button>
                          ) : (
                            <span className="text-slate-300">0</span>
                          )}
                        </td>
                      ))}
                      <td className="px-3 py-2.5 text-right tabular-nums font-bold text-slate-900">
                        {row.total}
                      </td>
                    </tr>
                  ))}
                </tbody>
                {summaryRows.length > 1 && (
                  <tfoot>
                    <tr className="border-t-2 border-slate-200 bg-slate-50/40 text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                      <td className="px-3 py-2 sticky left-0 bg-slate-50/40 z-10">All brands</td>
                      <td className="px-3 py-2 text-right tabular-nums">{summaryFooter.lt7}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{summaryFooter['7to30']}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{summaryFooter['30to90']}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{summaryFooter.gt90}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-900">{summaryFooter.total}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            )}
          </div>
        )}
      </Card>

      {/* Search + brand tabs */}
      <Card className="p-4 border-slate-200 rounded-2xl">
        <div className="flex items-center gap-3 mb-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Search by brand, ad text, advertiser name…"
              className="pl-10 h-10"
              data-testid="repo-search-input"
            />
          </div>
        </div>

        {/* Filter + sort row */}
        <div className="flex items-center gap-2 flex-wrap mb-3">
          {/* Segmented media-type filter */}
          <div className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50/70 p-0.5" role="tablist" aria-label="Filter by media type" data-testid="repo-media-filter">
            {[
              { key: 'all', label: 'All', count: filterCounts.total, icon: null },
              { key: 'image', label: 'Image', count: filterCounts.image, icon: <ImageIcon className="h-3 w-3" /> },
              { key: 'video', label: 'Video', count: filterCounts.video, icon: <Video className="h-3 w-3" /> },
            ].map(opt => {
              const active = mediaFilter === opt.key;
              return (
                <button
                  key={opt.key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setMediaFilter(opt.key)}
                  className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-xs font-medium transition-all ${
                    active ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200' : 'text-slate-500 hover:text-slate-700'
                  }`}
                  data-testid={`repo-media-filter-${opt.key}`}
                >
                  {opt.icon}
                  <span>{opt.label}</span>
                  <span className={`text-[10px] font-semibold tabular-nums ${active ? 'text-[#2EB4AC]' : 'text-slate-400'}`}>{opt.count}</span>
                </button>
              );
            })}
          </div>

          {/* Active-time filter */}
          <Select value={activeTimeFilter} onValueChange={setActiveTimeFilter}>
            <SelectTrigger className="h-9 w-auto min-w-[140px] rounded-full border-slate-200 bg-white text-xs font-medium text-slate-700" data-testid="repo-active-time-select">
              <Clock className="h-3.5 w-3.5 mr-1.5 text-slate-400" />
              <SelectValue placeholder="Any duration" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any duration</SelectItem>
              <SelectItem value="lt7">&lt; 7 days <span className="text-slate-400 ml-1">({filterCounts.lt7})</span></SelectItem>
              <SelectItem value="7to30">7 – 30 days <span className="text-slate-400 ml-1">({filterCounts['7to30']})</span></SelectItem>
              <SelectItem value="30to90">30 – 90 days <span className="text-slate-400 ml-1">({filterCounts['30to90']})</span></SelectItem>
              <SelectItem value="gt90">90+ days <span className="text-slate-400 ml-1">({filterCounts.gt90})</span></SelectItem>
            </SelectContent>
          </Select>

          {/* Sort */}
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="h-9 w-auto min-w-[150px] rounded-full border-slate-200 bg-white text-xs font-medium text-slate-700" data-testid="repo-sort-select">
              <ArrowUpDown className="h-3.5 w-3.5 mr-1.5 text-slate-400" />
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="recent">Most recent</SelectItem>
              <SelectItem value="oldest">Oldest first</SelectItem>
              <SelectItem value="longest">Longest running</SelectItem>
              <SelectItem value="shortest">Shortest running</SelectItem>
            </SelectContent>
          </Select>

          {/* Clear all */}
          {(mediaFilter !== 'all' || activeTimeFilter !== 'any' || sortBy !== 'recent') && (
            <button
              type="button"
              onClick={() => { setMediaFilter('all'); setActiveTimeFilter('any'); setSortBy('recent'); }}
              className="text-xs text-[#2EB4AC] font-medium hover:underline ml-1"
              data-testid="repo-clear-filters"
            >
              Clear filters
            </button>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setActiveBrand(null)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${activeBrand === null ? 'bg-[#2EB4AC] text-white shadow-sm' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
            data-testid="brand-tab-all"
          >
            All brands
          </button>
          {groups.map(g => (
            <button
              key={g.brand}
              onClick={() => setActiveBrand(g.brand)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${activeBrand === g.brand ? 'bg-[#2EB4AC] text-white shadow-sm' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              data-testid={`brand-tab-${g.brand}`}
            >
              {g.brand} <span className={`ml-1.5 ${activeBrand === g.brand ? 'text-white/80' : 'text-slate-400'}`}>· {g.count}</span>
            </button>
          ))}
        </div>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-400">
          <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading repository…
        </div>
      ) : filteredGroups.length === 0 ? (
        <Card className="p-12 text-center border-dashed border-2 border-slate-200 bg-slate-50/40">
          <Library className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm font-semibold text-slate-700">No ads filed yet</p>
          <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
            Go to <a href="/competitor-radar" className="text-[#2EB4AC] font-semibold hover:underline">Competitor Radar</a>, select interesting ads, click <span className="font-semibold">Apply brand</span> — they'll land here grouped by brand.
          </p>
        </Card>
      ) : (
        <div className="space-y-3 mt-2">
          {filteredGroups.map(g => {
            const expanded = !!expandedBrands[g.brand];
            const groupIds = g.items.map(a => a.id);
            const groupSelectedCount = groupIds.filter(id => selectedIds.includes(id)).length;
            const allGroupSelected = groupIds.length > 0 && groupSelectedCount === groupIds.length;
            return (
              <section key={g.brand} className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid={`repo-group-${g.brand}`}>
                <div className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors">
                  <button
                    type="button"
                    onClick={() => toggleBrand(g.brand)}
                    className="flex items-center gap-3 text-left flex-1 min-w-0"
                    data-testid={`repo-group-toggle-${g.brand}`}
                  >
                    {expanded
                      ? <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />
                      : <ChevronRight className="h-4 w-4 text-slate-400 shrink-0" />}
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#2EB4AC]/15 to-cyan-100 flex items-center justify-center shrink-0">
                      <Tag className="h-4 w-4 text-[#2EB4AC]" />
                    </div>
                    <div>
                      <h2 className="text-base font-bold text-slate-900">{g.brand}</h2>
                      <p className="text-[11px] text-slate-500">
                        {g.count} ads · {g.analyzed_count} analyzed
                        {groupSelectedCount > 0 && <span className="ml-1.5 text-[#2EB4AC] font-semibold">· {groupSelectedCount} selected</span>}
                      </p>
                    </div>
                  </button>
                  <div className="flex items-center gap-2 shrink-0">
                    {expanded && groupIds.length > 0 && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); toggleSelectGroup(g); }}
                        className="text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full bg-slate-100 text-slate-600 hover:bg-[#2EB4AC]/10 hover:text-[#2EB4AC] transition-colors"
                        data-testid={`repo-group-selectall-${g.brand}`}
                      >
                        {allGroupSelected ? 'Unselect all' : 'Select all'}
                      </button>
                    )}
                    <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full ${expanded ? 'bg-[#2EB4AC]/10 text-[#2EB4AC]' : 'bg-slate-100 text-slate-500'}`}>
                      {expanded ? 'Hide' : 'View'}
                    </span>
                  </div>
                </div>
                {expanded && (
                  <div className="px-4 pb-4 pt-1 border-t border-slate-100">
                    {g.items.length === 0 ? (
                      <p className="text-xs text-slate-400 px-2 py-4">No matches in this brand.</p>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 mt-3">
                        {g.items.map(ad => (
                          <MiniAdCard
                            key={ad.id}
                            ad={ad}
                            onClick={setSelectedAd}
                            onDelete={deleteAd}
                            onRescrape={rescrapeAd}
                            rescraping={rescrapingId === ad.id}
                            selectionMode={selectionMode}
                            selected={selectedIds.includes(ad.id)}
                            onToggleSelect={toggleSelect}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}

      {/* Shared ad detail modal — same as Competitor Radar */}
      <AdDetailModal
        ad={selectedAd}
        open={!!selectedAd}
        onOpenChange={(o) => !o && setSelectedAd(null)}
        onAnalyze={analyzeAd}
        onSaveLearning={saveLearning}
        onRephraseLearning={rephraseLearning}
        analyzing={analyzingId === selectedAd?.id}
      />

      {/* Sticky bulk-action toolbar */}
      {selectionMode && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 px-4 py-3 rounded-2xl bg-slate-900 text-white shadow-2xl shadow-slate-900/30 border border-slate-700 animate-in slide-in-from-bottom-4 fade-in"
          data-testid="repo-bulk-toolbar"
        >
          <div className="flex items-center gap-2 pr-3 border-r border-slate-700">
            <CheckSquare className="h-4 w-4 text-[#2EB4AC]" />
            <span className="text-sm font-semibold">
              {selectedIds.length} <span className="text-slate-400 font-normal">selected</span>
            </span>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={clearSelection}
            className="h-8 px-3 text-slate-300 hover:text-white hover:bg-slate-800 text-xs"
            data-testid="repo-bulk-clear"
          >
            <X className="h-3.5 w-3.5 mr-1" /> Clear
          </Button>
          <Button
            size="sm"
            onClick={bulkDelete}
            disabled={bulkDeleting}
            className="h-8 px-4 bg-red-500 hover:bg-red-600 text-white text-xs font-semibold disabled:opacity-50"
            data-testid="repo-bulk-delete"
          >
            {bulkDeleting
              ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Deleting…</>
              : <><Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete {selectedIds.length}</>}
          </Button>
        </div>
      )}
      </>)}
    </div>
  );
}
